import React, { useState, useEffect } from 'react';
import { getMe, getAllProvinces, getDistrictsByProvince } from '../services/api';
import Spinner from './Spinner';
import './ApplicantDetailsForm.css'; // Import the new CSS file

function ApplicantDetailsForm({ initialData, onSave, onProceed, isProceeding }) {
    const [formData, setFormData] = useState(initialData || {});
    const [errors, setErrors] = useState({});
    const [provinces, setProvinces] = useState([]);
    const [districts, setDistricts] = useState([]);
    const [selectedProvince, setSelectedProvince] = useState('');

    useEffect(() => {
        const fetchProvinces = async () => {
            try {
                const response = await getAllProvinces();
                setProvinces(response.data); 
            } catch (error) {
                console.error('Failed to fetch provinces', error);
            }
        };

        fetchProvinces();
    }, []);

    useEffect(() => {
        if (selectedProvince) {
            const fetchDistricts = async () => {
                try {
                    const response = await getDistrictsByProvince(selectedProvince);
                    setDistricts(response.data);
                } catch (error) {
                    console.error('Failed to fetch districts', error);
                }
            };

            fetchDistricts();
        }
    }, [selectedProvince]);

    useEffect(() => {
        const fetchUserData = async () => {
            try {
                const { data } = await getMe();
                const { name, gender, dateOfBirth, countryOfBirth, residentialAddress, email, provinceId, districtId } = data;
                const [firstName, ...lastName] = name.split(' ');
                setFormData(prevData => ({
                    ...prevData,
                    'first-name': firstName,
                    'last-name': lastName.join(' '),
                    gender,
                    dob: dateOfBirth,
                    'country-of-birth': countryOfBirth,
                    address: residentialAddress,
                    email,
                    province: provinceId ? String(provinceId) : prevData.province,
                    district: districtId ? String(districtId) : prevData.district,
                }));
                if (provinceId) {
                    setSelectedProvince(String(provinceId));
                }
            } catch (error) {
                console.error('Failed to fetch user data', error);
            }
        };

        fetchUserData();
    }, []);

    useEffect(() => {
        setFormData(initialData || {});
    }, [initialData]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prevData => ({ ...prevData, [name]: value }));
        if (name === 'province') {
            setSelectedProvince(value);
            setDistricts([]); // Clear districts when province changes
        }
        // Clear error for this field when user starts typing
        setErrors(prevErrors => ({ ...prevErrors, [name]: undefined }));
    };

    const validateForm = () => {
        const newErrors = {};
        const requiredFields = [
            'first-name', 'last-name', 'gender', 'dob', 'country-of-birth',
            'province', 'district', 'email'
        ];

        requiredFields.forEach(field => {
            if (!formData[field] || formData[field].trim() === '') {
                newErrors[field] = 'This field is required';
            }
        });
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = (e) => {
        e.preventDefault(); // Prevent default form submission
        if (validateForm()) {
            onSave(formData); // Save data to parent state
            onProceed(formData); // Pass formData directly to avoid stale state
        } else {
            alert('Please fill in all required fields.');
        }
    };

    return (
        <div className="container"> {/* Re-using container class from original HTML */}
            <div className="sidebar">
                <h3>Application Progress</h3>
                <div className="progress-item completed">
                    <span className="icon">✅</span> Service Passport
                </div>
                <div className="progress-item active">
                    <span className="icon">  </span> Applicant Details
                </div>
                <div className="progress-item">
                    <span className="icon">  </span> Application Form
                </div>
                <div className="progress-item">
                    <span className="icon">○</span> Eligibility Documents
                </div>
            </div>

            <div className="main-content">
                <div className="form-container">
                    <h1>Applicant Details</h1>
                    <p>Please complete all required fields to proceed with your application.</p>

                    <form onSubmit={handleSubmit}>
                        <label htmlFor="first-name">First Name*</label>
                        <input
                            type="text"
                            id="first-name"
                            name="first-name"
                            value={formData['first-name'] || ''}
                            onChange={handleChange}
                            className={errors['first-name'] ? 'input-error' : ''}
                            readOnly
                        />
                        {errors['first-name'] && <span className="error-message">{errors['first-name']}</span>}

                        <label htmlFor="last-name">Last Name*</label>
                        <input
                            type="text"
                            id="last-name"
                            name="last-name"
                            value={formData['last-name'] || ''}
                            onChange={handleChange}
                            className={errors['last-name'] ? 'input-error' : ''}
                            readOnly
                        />
                        {errors['last-name'] && <span className="error-message">{errors['last-name']}</span>}

                        <label htmlFor="gender">Gender*</label>
                        <select
                            id="gender"
                            name="gender"
                            value={formData['gender'] || ''}
                            onChange={handleChange}
                            className={errors['gender'] ? 'input-error' : ''}
                        >
                            <option value="" disabled>Select Gender</option>
                            <option value="Male">Male</option>
                            <option value="Female">Female</option>
                        </select>
                        {errors['gender'] && <span className="error-message">{errors['gender']}</span>}

                        <label htmlFor="dob">Date of Birth*</label>
                        <input
                            type="date"
                            id="dob"
                            name="dob"
                            value={formData['dob'] || ''}
                            onChange={handleChange}
                            className={errors['dob'] ? 'input-error' : ''}
                        />
                        {errors['dob'] && <span className="error-message">{errors['dob']}</span>}

                        <label htmlFor="country-of-birth">Country of Birth*</label>
                        <input
                            type="text"
                            id="country-of-birth"
                            name="country-of-birth"
                            value={formData['country-of-birth'] || ''}
                            onChange={handleChange}
                            className={errors['country-of-birth'] ? 'input-error' : ''}
                        />
                        {errors['country-of-birth'] && <span className="error-message">{errors['country-of-birth']}</span>}

                        <label htmlFor="province">Province*</label>
                        <select
                            id="province"
                            name="province"
                            value={formData['province'] || ''}
                            onChange={handleChange}
                            className={errors['province'] ? 'input-error' : ''}
                        >
                            <option value="" disabled>Select Province</option>
                            {provinces.map(province => (
                                <option key={province.provinceid} value={province.provinceid}>
                                    {province.province}
                                </option>
                            ))}
                        </select>
                        {errors['province'] && <span className="error-message">{errors['province']}</span>}

                        <label htmlFor="district">District*</label>
                        <select
                            id="district"
                            name="district"
                            value={formData['district'] || ''}
                            onChange={handleChange}
                            className={errors['district'] ? 'input-error' : ''}
                            disabled={!selectedProvince}
                        >
                            <option value="" disabled>Select District</option>
                            {districts.map(district => (
                                <option key={district.districtid} value={district.districtid}>
                                    {district.district}
                                </option>
                            ))}
                        </select>
                        {errors['district'] && <span className="error-message">{errors['district']}</span>}

                        <label htmlFor="address">Address</label>
                        <input
                            type="text"
                            id="address"
                            name="address"
                            value={formData['address'] || ''}
                            onChange={handleChange}
                        />

                        <label htmlFor="email">E-Mail Address*</label>
                        <input
                            type="email"
                            id="email"
                            name="email"
                            value={formData['email'] || ''}
                            onChange={handleChange}
                            className={errors['email'] ? 'input-error' : ''}
                        />
                        {errors['email'] && <span className="error-message">{errors['email']}</span>}

                        <button type="submit" className="submit-button" disabled={isProceeding}>
                            {isProceeding ? <Spinner /> : 'Proceed to Application'}
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
}

export default ApplicantDetailsForm;
